import { useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  FileAudio,
  Loader2,
  Pause,
  Play,
  PlayCircle,
  PhoneIncoming,
  PhoneOff,
  Square,
  StopCircle,
  Type,
  Variable as VariableIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import { usePromptUploads } from '@/features/voice-campaigns/api';
import type {
  TtsConvertRequest,
  TtsConvertResponse,
} from '@/features/voice-library/types';
import type { TtsFlowFormValues, TtsRow } from '../../types';

interface Props {
  values: TtsFlowFormValues;
  /** Editor uses this to update text-row preview caches when steps get
   *  synthesized through this pane. */
  onPatchRow: (index: number, patch: Partial<TtsRow>) => void;
}

type StepMode = 'prompt' | 'text' | 'variable';

interface ResolvedStep {
  index: number;
  mode: StepMode;
  label: string;
  detail?: string;
  url?: string;
  /** Variables can't be played — they hold no design-time audio. */
  playable: boolean;
}

const STEP_VISUAL: Record<
  StepMode,
  { bg: string; ring: string; icon: LucideIcon; label: string }
> = {
  prompt: {
    bg: 'bg-sky-500',
    ring: 'ring-sky-300',
    icon: FileAudio,
    label: 'Prompt',
  },
  text: {
    bg: 'bg-fuchsia-500',
    ring: 'ring-fuchsia-300',
    icon: Type,
    label: 'Text',
  },
  variable: {
    bg: 'bg-violet-500',
    ring: 'ring-violet-300',
    icon: VariableIcon,
    label: 'Variable',
  },
};

export function TtsFlowPreviewPane({ values, onPatchRow }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const prompts = usePromptUploads();

  /** Resolve each row into what the caller will actually hear. */
  const steps: ResolvedStep[] = useMemo(() => {
    return values.ttsRows.map((row, i): ResolvedStep => {
      if (row.ttsType === 'variable') {
        return {
          index: i,
          mode: 'variable',
          label: row.value ? `Slot ${row.value}` : 'Variable (no slot)',
          detail: row.variableType ? `Reads as ${row.variableType}` : undefined,
          playable: false,
        };
      }
      if (typeof row.text === 'string') {
        return {
          index: i,
          mode: 'text',
          label: row.text || '(no text yet)',
          detail: row.previewUrl ? 'Preview cached' : 'Synthesized on save',
          url: row.previewUrl,
          playable: Boolean(row.text && row.text.trim().length > 0),
        };
      }
      const match = (prompts.data ?? []).find(
        (p) => String(p.promptId) === row.value,
      );
      const url = (match as { promptUrl?: string } | undefined)?.promptUrl;
      return {
        index: i,
        mode: 'prompt',
        label: match?.fileName ?? match?.promptName ?? `#${row.value || '?'}`,
        detail: match ? undefined : row.value ? undefined : 'Pick a prompt',
        url,
        playable: Boolean(url),
      };
    });
  }, [values.ttsRows, prompts.data]);

  /* ---- Single-source playback coordinator ---- */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Stash the active playStep promise's resolver so stopAll can settle it
  // and let the play-all loop exit cleanly.
  const resolveRef = useRef<(() => void) | null>(null);
  const cancelRef = useRef(false);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState<'playAll' | 'single' | null>(null);
  const [synthesizingIndex, setSynthesizingIndex] = useState<number | null>(
    null,
  );

  const stopAll = () => {
    cancelRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    resolveRef.current?.();
    resolveRef.current = null;
    setPlayingIndex(null);
    setBusy(null);
  };

  const playStep = (url: string) =>
    new Promise<void>((resolve) => {
      // Stop anything else in flight before starting this clip.
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      resolveRef.current?.();
      resolveRef.current = resolve;

      const a = new Audio(url);
      audioRef.current = a;
      const settle = () => {
        a.removeEventListener('ended', settle);
        a.removeEventListener('error', settle);
        // Don't clear resolveRef here — stopAll already manages it. Just
        // resolve if we're still the active resolver.
        if (resolveRef.current === resolve) {
          resolveRef.current = null;
          resolve();
        }
      };
      a.addEventListener('ended', settle);
      a.addEventListener('error', settle);
      void a.play();
    });

  /** Resolve a playable URL for a step, synthesizing text rows on demand. */
  const resolveUrl = async (
    step: ResolvedStep,
    row: TtsRow,
  ): Promise<string | null> => {
    if (!step.playable) return null;
    if (step.url) return step.url;
    if (step.mode !== 'text' || !row.text) return null;
    if (!values.language || !values.gender) {
      throw new Error('Pick a language and a voice first.');
    }
    setSynthesizingIndex(step.index);
    try {
      const convertReq: TtsConvertRequest = {
        userId: Number(userId),
        gender: values.gender as TtsConvertRequest['gender'],
        textScript: row.text,
        characterCount: row.text.length,
        language: values.language,
        filename: `preview_step_${step.index + 1}_${Date.now()}`,
        category: 'tts',
      };
      const res = await api.post<TtsConvertResponse>(
        '/api/obd/convertion/texttospeech',
        convertReq,
      );
      if (res.status === 'fail') {
        throw new Error(res.msg || 'Synthesis failed.');
      }
      onPatchRow(step.index, {
        previewUrl: res.downloadUrl,
        previewTtsId: res.ttsId,
      });
      return res.downloadUrl;
    } finally {
      setSynthesizingIndex(null);
    }
  };

  const handlePlayStep = async (step: ResolvedStep) => {
    if (busy === 'single' && playingIndex === step.index) {
      stopAll();
      return;
    }
    if (busy) stopAll();
    setBusy('single');
    cancelRef.current = false;
    setPlayingIndex(step.index);
    try {
      const row = values.ttsRows[step.index]!;
      const url = await resolveUrl(step, row);
      if (!url) {
        toast.message('Nothing to play for this step.');
        return;
      }
      if (cancelRef.current) return;
      await playStep(url);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not play step.',
      );
    } finally {
      setBusy(null);
      setPlayingIndex(null);
    }
  };

  const handlePlayAll = async () => {
    if (busy === 'playAll') {
      stopAll();
      return;
    }
    if (busy) stopAll();
    if (steps.length === 0) return;
    setBusy('playAll');
    cancelRef.current = false;
    try {
      for (const step of steps) {
        if (cancelRef.current) break;
        setPlayingIndex(step.index);
        const row = values.ttsRows[step.index]!;
        try {
          const url = await resolveUrl(step, row);
          if (!url) continue; // variable / not picked — skip silently
          if (cancelRef.current) break;
          await playStep(url);
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : 'Could not play step.',
          );
          break;
        }
      }
    } finally {
      setBusy(null);
      setPlayingIndex(null);
      audioRef.current = null;
    }
  };

  const playAllActive = busy === 'playAll';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <PhoneIncoming className="h-3.5 w-3.5 text-muted-foreground" />
            Caller flow
          </CardTitle>
          <Button
            type="button"
            size="sm"
            variant={playAllActive ? 'destructive' : 'default'}
            onClick={handlePlayAll}
            disabled={steps.length === 0}
          >
            {playAllActive ? (
              <StopCircle className="h-3.5 w-3.5" />
            ) : (
              <PlayCircle className="h-3.5 w-3.5" />
            )}
            {playAllActive ? 'Stop' : 'Play all'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          What the caller hears, step by step. Click any step's ▶ to preview it
          alone.
        </p>
      </CardHeader>
      <CardContent>
        {steps.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card/50 py-6 text-center text-xs text-muted-foreground">
            Add a step on the left to see the caller flow here.
          </div>
        ) : (
          <ol className="relative">
            {/* Vertical connector line behind the nodes */}
            <span
              aria-hidden
              className="pointer-events-none absolute left-[19px] top-2 bottom-2 w-px bg-border"
            />

            {/* Entry chip */}
            <li className="relative mb-3 flex items-center gap-3">
              <span className="z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card text-muted-foreground">
                <PhoneIncoming className="h-4 w-4" />
              </span>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Call starts
              </div>
            </li>

            {steps.map((s) => {
              const visual = STEP_VISUAL[s.mode];
              const Icon = visual.icon;
              const isPlaying = playingIndex === s.index && busy != null;
              const isSynth = synthesizingIndex === s.index;
              return (
                <li
                  key={s.index}
                  className="relative mb-3 flex items-start gap-3"
                >
                  {/* Node */}
                  <button
                    type="button"
                    onClick={() => handlePlayStep(s)}
                    disabled={!s.playable && s.mode !== 'text'}
                    className={cn(
                      'group/node z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-all',
                      visual.bg,
                      isPlaying && 'ring-4',
                      isPlaying && visual.ring,
                      !s.playable &&
                        s.mode !== 'text' &&
                        'cursor-not-allowed opacity-60',
                      s.playable && 'hover:scale-105',
                    )}
                    aria-label={isPlaying ? 'Stop step' : 'Play step'}
                    title={isPlaying ? 'Stop' : 'Play this step'}
                  >
                    {isSynth ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isPlaying ? (
                      busy === 'playAll' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Square className="h-3 w-3" />
                      )
                    ) : (
                      <Icon className="h-4 w-4 group-hover/node:hidden" />
                    )}
                    {!isPlaying && !isSynth && (
                      <Play className="hidden h-4 w-4 group-hover/node:block" />
                    )}
                  </button>

                  {/* Card body */}
                  <div
                    className={cn(
                      'min-w-0 flex-1 rounded-md border bg-card px-2.5 py-1.5 transition-colors',
                      isPlaying
                        ? 'border-primary ring-1 ring-primary/40'
                        : 'border-border',
                    )}
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <span>Step {s.index + 1}</span>
                      <span>·</span>
                      <span className={STEP_VISUAL[s.mode].bg.replace('bg-', 'text-')}>
                        {visual.label}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'truncate text-xs font-medium text-foreground',
                        s.label.startsWith('(') && 'text-muted-foreground italic',
                      )}
                      title={s.label}
                    >
                      {s.label}
                    </div>
                    {s.detail && (
                      <div className="truncate text-[10px] text-muted-foreground">
                        {s.detail}
                      </div>
                    )}
                  </div>

                  {/* Down arrow tucked between nodes (visual flow cue) */}
                  <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute -bottom-3 left-[14px] h-3 w-3 text-border"
                  />
                </li>
              );
            })}

            {/* Hangup chip — implicit end of every flow */}
            <li className="relative mt-1 flex items-center gap-3">
              <span className="z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card text-muted-foreground">
                <PhoneOff className="h-4 w-4" />
              </span>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Call ends
              </div>
            </li>
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
